import { Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import type { NumberFormatterOptions } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectStatementDataColumns, selectStatementLines } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectFieldCaptionLookup } from "../../../../store/selectors/metaDataSelectors.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import { getDataColumnLabel } from "../../../viewer/financialStatement/statementColumnLabels.ts";
import FormatSettings from "../../common/fields/format/FormatSettings.tsx";
import type { FormatBlockSource } from "../utils/statementFormat.ts";
import { fixedCurrenciesOnly, getFormatBlocks } from "../utils/statementFormat.ts";
import { getStatementLineLabel } from "../utils/statementRowsArea.ts";

interface Props {
  readonlyMode: boolean;
}

const FinancialStatementFormatContainer = ({ readonlyMode }: Props) => {
  const dispatch = useAppDispatch();
  const lines = useSelector(selectStatementLines);
  const dataColumns = useSelector(selectStatementDataColumns);
  const getFieldCaption = useSelector(selectFieldCaptionLookup);

  const blocks = getFormatBlocks(lines, dataColumns);

  const getBlockGuid = (block: FormatBlockSource): string =>
    block.kind === "line" ? block.line.guid : block.column.guid;

  const getBlockLabel = (block: FormatBlockSource): string =>
    block.kind === "line"
      ? getStatementLineLabel(block.line, getFieldCaption)
      : getDataColumnLabel(block.column);

  const getBlockFormat = (block: FormatBlockSource): NumberFormatterOptions | undefined =>
    block.kind === "line" ? block.line.format : block.column.format;

  const handleUpdateFormatter = (block: FormatBlockSource, format: NumberFormatterOptions | undefined) => {
    if (block.kind === "line") {
      dispatch(createFinancialStatementAction("updateLine", { guid: block.line.guid, changes: { format } }));
    } else {
      dispatch(createFinancialStatementAction("updateDataColumn", { guid: block.column.guid, changes: { format } }));
    }
  };

  if (blocks.length === 0) {
    return (
      <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <Typography color="textSecondary">
          No amount lines yet — a line's number format is set here once the statement has amounts.
        </Typography>
      </Stack>
    );
  }

  return (
    <ScrollableFlexContainer scrollContainerSx={{ px: "1.3rem" }}>
      <Stack sx={{ gap: 1 }}>
        {blocks.map((block) => (
          <FormatSettings
            key={getBlockGuid(block)}
            Label={
              <Typography variant="subtitle2" color="textPrimary">
                {getBlockLabel(block)}
              </Typography>
            }
            format={getBlockFormat(block)}
            currencyDimensions={fixedCurrenciesOnly}
            onUpdateFormatter={(format) => handleUpdateFormatter(block, format)}
            readonlyMode={readonlyMode}
          />
        ))}
      </Stack>
    </ScrollableFlexContainer>
  );
};

export default FinancialStatementFormatContainer;
